import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { EquipmentService } from '../../services/equipment/equipment.service';
import { NameService } from '../../services/name/name.service';
import { RecordingService } from '../../services/recording/recording.service';
import { RolesService } from '../../services/roles/roles.service';
import { SessionsService } from '../../services/sessions/sessions.service';
import { UserService } from '../../services/user/user.service';
import { WindowToken } from '../../services/window/window';
import {
  Roles,
  RoleLimits,
  VideoResolutions,
  VideoResolutionsForDisplay,
  MiniNotification,
  MiniNoteType,
} from '@sc/types';
import { Equipment, Prompt, PromptTypes } from '@sc/types';
import { WalletService } from '../../services/wallet/wallet.service';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { PromptsService } from '../../services/prompts/prompts.service';

@Component({
  selector: 'sc-participant-equipment',
  templateUrl: './participant-equipment.component.html',
  styleUrls: ['./participant-equipment.component.scss'],
})
export class ParticipantEquipmentComponent implements OnInit, OnDestroy {
  @Input() equipment$: Observable<Equipment>;
  @Input() uid: string;
  @Input() expanded = false;

  plan$ = this.walletService.studioPlan$;
  role$ = this.rolesService.role$;
  roleLimits = RoleLimits;
  roles = Roles;
  VideoResolutions = VideoResolutions;
  VideoResolutionsForDisplay = VideoResolutionsForDisplay;

  sessionEchoCancellation: boolean;
  recording$ = this.recordingService.recording$;
  studioSession$ = this.sessionsService.studioSession$;
  activeUser$ = this.userService.activeUser$;
  isVoiceConf: boolean;

  osIcon = '';
  browserIcon = '';
  isWindows = false;

  equipment: Equipment;
  selectedAudioIn: string;
  selectedVideoIn: string;
  selectedAudioOut: string;

  recordingResolution;
  displayResolution: VideoResolutionsForDisplay;
  videoResolutions = Object.keys(VideoResolutions) as VideoResolutions[];

  filterDeviceName = this.equipmentService.filterDeviceName;

  subs: Array<Subscription> = [];

  constructor(
    private analyticsService: AnalyticsService,
    private equipmentService: EquipmentService,
    private promptsService: PromptsService,
    private nameService: NameService,
    private recordingService: RecordingService,
    private rolesService: RolesService,
    private sessionsService: SessionsService,
    private toastrService: ToastrService,
    private walletService: WalletService,
    private userService: UserService,
    @Inject(WindowToken) private window: Window
  ) {}

  ngOnInit() {
    this.setupDeviceInfo();
    this.setupSessionEchoCancellation();
  }

  async echoCancellationToggled(event) {
    const value = event.detail.checked;
    const equipment = await firstValueFrom(this.equipment$);

    if (value === equipment.echoCancellation) return;
    this.equipmentService.setEchoCancellation(value, null, this.uid);
    await this.analyticsService.track('toggled echo cancellation from participant equipment panel', {
      enabled: value,
      uid: this.uid,
    });
  }

  async selectAudioIn(event) {
    if (this.uid === this.activeUser$.value.uid) {
      const equipment = this.equipmentService.systemDevices$.value.find((device) => device.deviceId === event.deviceId);
      this.equipmentService.selectedMicrophone$.next(equipment);
    } else {
      setTimeout(() => {
        this.selectedAudioIn = this.equipment.audioIn.deviceId;
      }, 0);
      const name = await firstValueFrom(
        this.nameService.getName(this.studioSession$.value.sessionID, this.activeUser$.value.uid)
      );
      const prompt: Prompt = {
        title: `${name} is requesting that you change your microphone`,
        message: `Do you want to change your current microphone to ${event.label}?`,
        creator: this.activeUser$.value.uid,
        data: { ...event, kind: 'audioinput' },
        presentAs: `toast`,
        type: PromptTypes.CHANGEEQUIPMENT,
        targetParticipants: [this.uid],
      };

      await this.sessionsService.createSessionPrompt(prompt);
      await this.analyticsService.track('prompted participant to change microphone', {
        creatorUID: this.activeUser$.value.uid,
        targetParticipantUID: this.uid,
      });
    }
  }

  async selectVideoIn(event) {
    if (this.uid === this.activeUser$.value.uid) {
      const equipment = this.equipmentService.systemDevices$.value.find((device) => device.deviceId === event.deviceId);
      this.equipmentService.selectedCamera$.next(equipment);
    } else {
      setTimeout(() => {
        this.selectedVideoIn = this.equipment.videoIn.deviceId;
      }, 0);
      const name = await firstValueFrom(
        this.nameService.getName(this.studioSession$.value.sessionID, this.activeUser$.value.uid)
      );
      const prompt: Prompt = {
        title: `${name} is requesting that you change your camera`,
        message: `Do you want to change your current camera to ${event.label}?`,
        creator: this.activeUser$.value.uid,
        data: { ...event, kind: 'videoinput' },
        presentAs: `toast`,
        type: PromptTypes.CHANGEEQUIPMENT,
        targetParticipants: [this.uid],
      };

      await this.sessionsService.createSessionPrompt(prompt);
      await this.analyticsService.track('prompted participant to change camera', {
        creatorUID: this.activeUser$.value.uid,
        targetParticipantUID: this.uid,
      });
    }
  }

  async selectAudioOut(event) {
    if (this.uid === this.activeUser$.value.uid) {
      const equipment = this.equipmentService.systemDevices$.value.find((device) => device.deviceId === event.deviceId);
      this.equipmentService.selectedHeadphones$.next(equipment);
    } else {
      setTimeout(() => {
        this.selectedAudioOut = this.equipment.audioOut.deviceId;
      }, 0);
      const name = await firstValueFrom(
        this.nameService.getName(this.studioSession$.value.sessionID, this.activeUser$.value.uid)
      );
      const prompt: Prompt = {
        title: `${name} is requesting that you change your audio output device`,
        message: `Do you want to change your current audio output to ${event.label}?`,
        creator: this.activeUser$.value.uid,
        data: { ...event, kind: 'audiooutput' },
        presentAs: `toast`,
        type: PromptTypes.CHANGEEQUIPMENT,
        targetParticipants: [this.uid],
      };

      await this.sessionsService.createSessionPrompt(prompt);
      await this.analyticsService.track('prompted participant to change audio output', {
        creatorUID: this.activeUser$.value.uid,
        targetParticipantUID: this.uid,
      });
    }
  }

  setupDeviceInfo() {
    const sub = this.equipment$.subscribe(async (equipment) => {
      this.equipment = equipment;
      if (!equipment) return;
      const os = equipment?.deviceInfo?.os;
      const os_version = equipment?.deviceInfo?.os_version;
      const browser = equipment?.deviceInfo?.browser;

      try {
        if (equipment.audioIn?.deviceId) this.selectedAudioIn = equipment.audioIn.deviceId;
        if (equipment.videoIn?.deviceId) this.selectedVideoIn = equipment.videoIn.deviceId;
        if (equipment.audioOut?.deviceId) this.selectedAudioOut = equipment.audioOut.deviceId;
        if (equipment.videoResolution) this.recordingResolution = this.getDisplayResolution(equipment.videoResolution);
        if (equipment.videoTrackSettings) {
          this.displayResolution =
            VideoResolutionsForDisplay[this.equipmentService.getVideoTrackResolution(equipment.videoTrackSettings)];
        }
        // if (browser !== 'Chrome') this.equipmentService.setPCMAudio(false, null, this.uid);
        if (os) {
          switch (os) {
            case 'Mac':
            case 'iOS':
            case 'iPad':
            case 'iPhone':
              this.osIcon = 'apple2.svg';
              break;
            case 'Windows':
              this.osIcon = 'windows.svg';
              this.isWindows = true;
              break;
            case 'Android':
              this.osIcon = 'android.svg';
              break;
            default:
              this.osIcon = 'globe.svg';
              break;
          }
        }
        if (browser) {
          switch (browser) {
            case 'Chrome':
              this.browserIcon = 'chrome.svg';
              break;
            case 'Edge':
            case 'MS-Edge':
              this.browserIcon = 'edge.svg';
              break;
            case 'Firefox':
              this.browserIcon = 'firefox.svg';
              break;
            case 'Opera':
              this.browserIcon = 'opera.svg';
              break;
            case 'Safari':
              this.browserIcon = 'safari.svg';
              break;
            default:
              this.browserIcon = 'globe.svg';
              break;
          }
        }
      } catch (error) {
        const name = await firstValueFrom(
          this.nameService.getName(this.studioSession$.value.sessionID, this.activeUser$.value.uid)
        ).catch(() => {
          return undefined;
        });
        if (os_version?.includes('15') && browser == 'Chrome' && name) {
          const errorPrompt: Prompt = {
            title: `We failed to retrieve ${name} equipment information!`,
            message: `We caught an error with ${name}'s equipment. Seems ${equipment.deviceInfo.os_version} is having issues with ${browser}. Please try ask them to try to rejoin on a different browser if the issues persists. Full Error: ${error.message}`,
            creator: this.activeUser$.value.uid,
            presentAs: `toast`,
            type: PromptTypes.NOTIFYHOSTS,
            targetRole: Roles.SHOW_MANAGER,
            resolved: false,
          };
          this.promptsService.notifyHostsOfWarningHandler(errorPrompt);
          const notification: MiniNotification = {
            label: `We failed to retrieve ${name} equipment information!`,
            icon: 'upload.svg',
            color: 'error',
            role: Roles.SHOW_MANAGER,
            topic: 'selectEquipment',
          };
          this.sessionsService.setParticipantNotification(
            MiniNoteType.QUALITY,
            notification,
            this.studioSession$.value.sessionID,
            this.activeUser$.value.uid
          );
          this.toastrService.warning(
            `Failed to retrieve equipment information. ${equipment.deviceInfo.os_version} is having issues with ${browser}. Please try to join again on a different browser. Error: ${error.message}`,
            'Equipment Error',
            {
              progressBar: true,
              progressAnimation: 'decreasing',
              closeButton: true,
              tapToDismiss: false,
              timeOut: 10 * 1000,
              toastComponent: GeneralToastComponent,
            }
          );
        } else {
          this.toastrService.error(
            `Something went wrong with attempting to connect to your equipment. Please ty to join again on a different browser. Error: ${error.message}`,
            'Equipment Error',
            {
              progressBar: true,
              progressAnimation: 'decreasing',
              closeButton: true,
              tapToDismiss: false,
              timeOut: 10 * 1000,
              toastComponent: GeneralToastComponent,
            }
          );
        }

        this.analyticsService.track('failed to retrieve equipment information', {
          uid: this.uid,
          error: error.message,
        });

        return;
      }
    });

    this.subs.push(sub);
  }

  async setupSessionEchoCancellation() {
    const sub = this.studioSession$.subscribe((session) => {
      if (!session) return;
      this.sessionEchoCancellation =
        session.sessionEchoCancellation !== null && session.sessionEchoCancellation !== undefined;
      this.isVoiceConf = session.confType === 'voice';
    });
    this.subs.push(sub);
  }

  async selectRecordingResolution(event) {
    const value = this.recordingResolution;
    const equipment = await firstValueFrom(this.equipment$);
    if (value === equipment.videoResolution) return;

    if (!this.plan$.value?.videoUHD && value === VideoResolutions.UHD) {
      const toast: ActiveToast<GeneralToastComponent> = this.toastrService.info(
        'You must upgrade to a paid Descript plan to enable 4K recording.',
        'Upgrade Plan',
        {
          progressBar: true,
          closeButton: true,
          tapToDismiss: false,
          disableTimeOut: true,
          timeOut: 30 * 1000,
          toastComponent: GeneralToastComponent,
        }
      );

      toast.toastRef.componentInstance.buttons = [
        {
          label: 'Upgrade',
          handler: () => {
            this.window.open('/account/subscription', '_blank');
          },
        },
      ];
      this.recordingResolution = this.equipmentService.selectedResolution$.value;
      return;
    }

    if (!this.checkSupported(value)) return;
    this.equipmentService.setVideoQuality(value, null, this.uid);
    await this.analyticsService.track('selected camera resolution from participant equipment panel', {
      resolution: value,
      uid: this.uid,
      requester: this.activeUser$.value.uid,
      label: this.equipment.videoIn.label,
    });
  }

  checkSupported(resolution: VideoResolutions) {
    if (resolution === VideoResolutions.UHD && this.isWindows && !this.studioSession$.value.windows4KOverride) {
      return false;
    }
    return this.equipment?.supportedResolutions?.includes(resolution);
  }

  getDisplayResolution(resolution: VideoResolutions) {
    return VideoResolutionsForDisplay[resolution];
  }

  async pcmAudioToggled(event) {
    const value = event.detail.checked;
    const equipment = await firstValueFrom(this.equipment$);
    if (equipment.pcmAudio === value) return;

    this.equipmentService.setPCMAudio(value, null, this.uid);
    await this.analyticsService.track('toggled pcm audio from participant equipment panel', {
      enabled: value,
      uid: this.uid,
    });
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
